
import React from 'react'
import { Link } from 'react-router-dom'
import {Download, Users, ArrowRight, BookOpen, Globe, FileText, Award} from 'lucide-react'

const Resources = () => {
  // Analytics handler for download tracking
  const handleDownloadClick = (downloadId: string, title: string) => {
    if (typeof window !== 'undefined' && (window as any).dataLayer) {
      (window as any).dataLayer.push({
        event: 'resource_download',
        resource_id: downloadId,
        resource_title: title
      })
    } else {
      console.debug(`[Analytics] Resource download: ${downloadId} - ${title}`)
    }
  }

  const resources = [
    {
      title: "NCLEX Quick Start Guide",
      description: "Step-by-step overview of the NCLEX process, registration, prep roadmap, and FAQs.",
      metadata: "Updated Nov 2025 • PDF (Google Drive)",
      icon: <BookOpen className="h-8 w-8 text-primary-teal" />,
      downloadUrl: "https://drive.google.com/uc?export=download&id=1CYpQL358Zi6Xj28mJgTrDtihElsFX60D",
      downloadId: "nclex-quick-start-guide"
    },
    {
      title: "CGFNS & VisaScreen Checklist",
      description: "Credential evaluation + VisaScreen requirements with a documents checklist and decision flow.",
      metadata: "Updated Nov 2025 • PDF (Google Drive)",
      icon: <FileText className="h-8 w-8 text-primary-teal" />,
      downloadUrl: "https://drive.google.com/uc?export=download&id=1qCRT22_yz9A_bEjwI90Kwsmw73YSCR0d",
      downloadId: "cgfns-visascreen-checklist"
    },
    {
      title: "GCC Dataflow Verification Steps",
      description: "Dataflow PSV for Saudi, UAE, Qatar, Oman, Bahrain, Kuwait — costs, timelines, appeals, and transfers.",
      metadata: "Updated Nov 2025 • PDF (Google Drive)",
      icon: <Award className="h-8 w-8 text-primary-teal" />,
      downloadUrl: "https://drive.google.com/uc?export=download&id=1qw9gk0J7FvHJkvpKladaqPd2tKs5LBID",
      downloadId: "gcc-dataflow-verification"
    },
    {
      title: "U.S. vs U.K. vs GCC — 2025 Comparison Guide",
      description: "Side-by-side routes, cost, time, salary, ROI, exam difficulty, red flags, and next steps.",
      metadata: "Updated Nov 2025 • PDF (Google Drive)",
      icon: <Globe className="h-8 w-8 text-primary-teal" />,
      downloadUrl: "https://drive.google.com/uc?export=download&id=137PO-ZpnM4orS9g6jfRIuZoscn3ic62l",
      downloadId: "us-uk-gcc-comparison-2025"
    }
  ]

  return (
    <div className="min-h-screen py-20 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-primary-navy mb-6">
            Resources to progress faster
          </h1>
          <p className="text-xl text-primary-slate max-w-3xl mx-auto">
            Step-by-step guides and checklists to reach 'Verified' status and advance your global nursing career.
          </p>
        </div>

        {/* Update Notice */}
        <div className="text-center mb-8">
          <p className="text-primary-slate text-sm">
            These guides are updated quarterly to reflect the latest NCLEX, CGFNS, Dataflow, and visa requirements.
          </p>
        </div>

        {/* Resource Cards */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {resources.map((resource, index) => (
            <div key={index} className="bg-white border border-gray-200 rounded-xl p-8 hover:shadow-lg transition-shadow">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  {resource.icon}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-primary-navy mb-2">
                    {resource.title}
                  </h3>
                  <p className="text-primary-slate mb-2">
                    {resource.description}
                  </p>
                  <p className="text-sm text-gray-500 mb-1">
                    {resource.metadata}
                  </p>
                  <p className="text-xs text-gray-500 mb-4">
                    Hosted on Google Drive — opens in a new tab
                  </p>
                  <a
                    href={resource.downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-download-id={resource.downloadId}
                    onClick={() => handleDownloadClick(resource.downloadId, resource.title)}
                    className="bg-primary-teal text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-navy transition-colors inline-flex items-center"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    ⬇️ Download (PDF)
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* WhatsApp Community Section */}
        <div className="bg-primary-light rounded-2xl p-8 md:p-12 mb-16">
          <div className="text-center">
            {/* WhatsApp Icon */}
            <div className="w-16 h-16 bg-[#25D366] rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.891 3.488"/>
              </svg>
            </div>
            
            <h2 className="text-3xl font-bold text-primary-navy mb-4">
              Join Our Nursing Hive Community 💬
            </h2>
            <p className="text-primary-slate mb-2 max-w-2xl mx-auto">
              Stay connected with nurses across Pakistan, India, and GCC.
            </p>
            <p className="text-primary-slate mb-8 max-w-2xl mx-auto">
              Get updates, resources, and mentorship directly in our official WhatsApp group.
            </p>
            
            <a
              href="https://chat.whatsapp.com/G9KobSl1sV08fPw5m3GIwy?mode=wwt"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#25D366] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#1DA851] transition-colors inline-flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.891 3.488"/>
              </svg>
              Join WhatsApp Group
            </a>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center bg-white border border-primary-teal rounded-2xl p-8 md:p-12">
          <h2 className="text-3xl font-bold text-primary-navy mb-4">
            Ready for personalized guidance?
          </h2>
          <p className="text-primary-slate mb-8 max-w-2xl mx-auto">
            Create your profile to see your personalized next step and get tailored resources for your specific situation.
          </p>
          <Link
            to="/signup"
            className="bg-primary-teal text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-primary-navy transition-colors inline-flex items-center"
          >
            Register Free / Sign In
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </div>
    </div>
  )
}

export default Resources
